import React, { useState, useMemo } from 'react';
import { Box, Button, Typography, IconButton, CircularProgress } from '@mui/material';
import { ChevronLeft, ChevronRight, CheckCircle } from '@material-ui/icons';
import RazProgressSVG from '../../../../components/UI/SVG_IMGs/RazProgressSVG';
import StudentScoreboardSampleImg from '../../../../assets/images/studentScoreboardSampleImg.png';
import { useColorMode } from '../../../../context/colorMode/colorMode.context';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

// ── Sample questions (5 base, duplicated + shuffled to 10) ────────────────────

const BASE_QUESTIONS = [
    'I talk to my child about the dangers related to the Internet (e.g., cyber-bullying, game addiction, privacy violations).',
    'I talk to my child about how to decide if online information can be trusted or not.',
    "I discuss with my child how what happens in online videos (e.g., movies, online streaming, shows, social media) wouldn't happen in real life.",
    'I discuss with my child how online videos (e.g., movies, online streaming, shows, social media) are made.',
    'I talk to my child about what personal info should or should not be posted online.',
];

const OPTIONS = ['Never', 'Rarely', 'Sometimes', 'Often', 'Always'];

// Same accent + brand-var language as the rest of this tab strip (CpaPackagePanel)
// and the redesigned Assessment Preview / Assess-tab pages — was previously a
// one-off coral palette that didn't match its own parent tabs or support dark mode.
const ACCENT = 'var(--brand-primary, #FF6900)';

// ── Component ─────────────────────────────────────────────────────────────────

const CpaAssessmentPreview = ({ onExit }) => {
    const { isDark } = useColorMode();
    const questions = useMemo(() => {
        const doubled = [...BASE_QUESTIONS, ...BASE_QUESTIONS];
        return doubled.sort(() => Math.random() - 0.5);
    }, []);

    const [currentQ, setCurrentQ] = useState(0);
    const [answers, setAnswers] = useState({});

    const total = questions.length;           // 10 questions (indices 0–9)
    const SCOREBOARD_IDX = total;             // index 10  → scoreboard slide
    const isScoreboard = currentQ === SCOREBOARD_IDX;

    const canGoBack = currentQ > 0;
    const canGoNext = currentQ < SCOREBOARD_IDX;

    const goNext = () => { if (canGoNext) setCurrentQ((q) => q + 1); };
    const goPrev = () => { if (canGoBack) setCurrentQ((q) => q - 1); };

    const handleSelect = (option) => {
        setAnswers((prev) => ({ ...prev, [currentQ]: option }));
    };

    // ── Shared nav-button style helpers ───────────────────────────────────────
    const navBtnSx = (active) => ({
        bgcolor: active ? 'var(--brand-surface, #fff)' : 'transparent',
        border: `1px solid ${ACCENT}`,
        color: active ? ACCENT : 'var(--brand-text-muted, #D1D5DB)',
        width: 44,
        height: 44,
        boxShadow: active ? '0 2px 8px rgba(255,105,0,0.25)' : 'none',
        '&:hover': { bgcolor: ACCENT, color: '#fff', borderColor: ACCENT },
    });

    // ── Question screen / scoreboard ──────────────────────────────────────────
    return (
        <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            overflow: 'hidden',
        }}>
            {/* ── Left nav button ───────────────────────────────────────────── */}
            <Box sx={{ flexShrink: 0, px: 2, display: 'flex', alignItems: 'center', height: '100%' }}>
                <IconButton onClick={goPrev} disabled={!canGoBack} sx={navBtnSx(canGoBack)}>
                    <ChevronLeft style={{ fontSize: 26 }} />
                </IconButton>
            </Box>

            {/* ── Center content ────────────────────────────────────────────── */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>

                {/* ── Scoreboard slide ──────────────────────────────────────── */}
                {isScoreboard && (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {/* Compact header */}
                        <Box sx={{ flexShrink: 0, textAlign: 'center', pt: 2.5, pb: 1 }}>
                            <Typography sx={{ fontWeight: 800, fontSize: 20, color: 'var(--brand-text, #111827)' }}>
                                Sample Scoreboard
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #6B7280)', mt: 0.5, lineHeight: 1.5 }}>
                                This is how a learner&apos;s results look after completing the full assessment.
                            </Typography>
                        </Box>

                        {/* Image — fills remaining height, scrolls if taller than viewport */}
                        <Box sx={{ flex: 1,alignSelf: "center", overflowY: 'auto', px: 3, pb: 1 }}>
                            <img
                                src={StudentScoreboardSampleImg}
                                alt="Student scoreboard sample"
                                style={{
                                    width: '90%',
                                    marginLeft: 'auto',
                                    marginRight: 'auto',
                                    height: 'auto',
                                    display: 'block',
                                    borderRadius: 12,
                                    boxShadow: '0 2px 16px rgba(0,0,0,0.10)',
                                }}
                            />
                        </Box>

                        {/* Back button — pinned to bottom */}
                        <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'center', py: 2 }}>
                            <Button
                                variant="contained"
                                disableElevation
                                onClick={onExit}
                                sx={{
                                    bgcolor: ACCENT,
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: 14,
                                    px: 4,
                                    py: 1.3,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    '&:hover': { bgcolor: '#E65C00' },
                                }}
                            >
                                Back to Package Info
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* ── Question slide — same header + option-list treatment as the
                    Assessment Preview page / Assess tab (question number badge +
                    circular progress on the right, vertical lettered options). */}
                {!isScoreboard && (
                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'center', px: { xs: 3, sm: 4 }, py: 3 }}>
                        <Box sx={{ maxWidth: '960px', mx: 'auto', width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'stretch', gap: { xs: 2, sm: 3 } }}>
                                {/* Question number */}
                                <Box sx={{
                                    flexShrink: 0, textAlign: 'center',
                                    pr: { xs: 2, sm: 3 }, borderRight: '2px solid var(--brand-border, #E5E7EB)',
                                    display: 'flex', flexDirection: 'column', justifyContent: 'center',
                                }}>
                                    <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                        Question
                                    </Typography>
                                    <Typography sx={{ fontSize: { xs: 34, sm: 44 }, fontWeight: 800, lineHeight: 1, color: ACCENT }}>
                                        {String(currentQ + 1).padStart(2, '0')}
                                    </Typography>
                                    <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                        of {total}
                                    </Typography>
                                </Box>

                                {/* Question text */}
                                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                    <Typography sx={{ fontSize: { xs: '20px', sm: '24px' }, fontWeight: 800, color: 'var(--brand-text, #111827)', lineHeight: 1.3, textAlign: 'left' }}>
                                        {questions[currentQ]}
                                    </Typography>
                                </Box>

                                {/* Right: circular progress with raaz icon */}
                                <Box sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    <Box sx={{ position: 'relative', width: 76, height: 76 }}>
                                        <CircularProgress variant="determinate" value={100} size={76} thickness={3.5}
                                            sx={{
                                                position: 'absolute', left: 0, top: 0,
                                                '& .MuiCircularProgress-circle': { stroke: isDark ? 'rgba(255,255,255,0.14)' : '#F0F0F0' },
                                            }} />
                                        <CircularProgress variant="determinate" value={((currentQ + 1) / total) * 100} size={76} thickness={3.5}
                                            sx={{
                                                position: 'absolute', left: 0, top: 0,
                                                '& .MuiCircularProgress-circle': { stroke: '#FF6900', strokeLinecap: 'round' },
                                            }} />
                                        {/* Backdrop behind the mascot so it doesn't float edge-to-edge
                                            against a dark page background — subtle brand-surface tint. */}
                                        <Box sx={{
                                            position: 'absolute', inset: 8, borderRadius: '50%',
                                            backgroundColor: isDark ? 'rgba(255,105,0,0.12)' : 'rgba(255,105,0,0.06)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <RazProgressSVG color="#FF6900" style={{ width: 42, height: 42 }} />
                                        </Box>
                                    </Box>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: ACCENT, mt: 0.5 }}>
                                        {Math.round(((currentQ + 1) / total) * 100)}%
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Answer options — vertical lettered list, same as renderOptions() */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 3 }}>
                                {OPTIONS.map((opt, i) => {
                                    const selected = answers[currentQ] === opt;
                                    return (
                                        <Box
                                            key={opt}
                                            onClick={() => handleSelect(opt)}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 2,
                                                p: '14px 20px', borderRadius: '12px', cursor: 'pointer',
                                                border: selected ? `2px solid ${ACCENT}` : '1px solid var(--brand-border, #E5E7EB)',
                                                backgroundColor: selected ? 'rgba(255,105,0,0.06)' : 'var(--brand-surface, #ffffff)',
                                                transition: 'all 0.15s ease',
                                                '&:hover': { borderColor: ACCENT, boxShadow: '0px 2px 8px rgba(0,0,0,0.04)' },
                                            }}
                                        >
                                            <Box sx={{
                                                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontWeight: 700, fontSize: 15,
                                                border: selected ? 'none' : '1.5px solid var(--brand-border, #D1D5DB)',
                                                backgroundColor: selected ? ACCENT : 'transparent',
                                                color: selected ? '#ffffff' : 'var(--brand-text-muted, #6B7280)',
                                            }}>
                                                {LETTERS[i] || i + 1}
                                            </Box>
                                            <Typography sx={{ flex: 1, fontSize: 16, fontWeight: selected ? 700 : 500, color: 'var(--brand-text, #111827)', textAlign: 'left' }}>
                                                {opt}
                                            </Typography>
                                            {selected && <CheckCircle style={{ color: ACCENT, fontSize: 24 }} />}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>{/* end center content */}

            {/* ── Right nav button ──────────────────────────────────────────── */}
            <Box sx={{ flexShrink: 0, px: 2, display: 'flex', alignItems: 'center', height: '100%' }}>
                <IconButton onClick={goNext} disabled={!canGoNext} sx={navBtnSx(canGoNext)}>
                    <ChevronRight style={{ fontSize: 26 }} />
                </IconButton>
            </Box>
        </Box>
    );
};

export default CpaAssessmentPreview;
